import React from "react";
import { Box, Alert, Stack } from "@mui/material";
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { Currency, ProducerDashboard } from "../interfaces/ProducerDashboard";
import { formatMoney, isApiError, isStatusDesync } from "./ProducerDashboardHelper";
import ProducerBalances from "./ProducerBalances";
import AssociatedOperators from "./AssociatedOperators";
import SyncStatus from "./SyncStatus";
import Transactions from "./Transactions";
import KycDocuments from "./KycDocuments";
import PayableInvoices from "./PayableInvoices";
import { ApiErrorAlert } from "./ApiErrorAlert";
import DashboardTransactions from "./onecomm-transactions/DashboardTransactions";

type ProducerDashboardContentProps = {
    dashboardData: ProducerDashboard;
    currency: Currency;
    institutionId: string;
    handleSyncStatus: () => void;
    isUpdating: boolean;
    producerUid: string;
}


export default function ProducerDashboardContent({ dashboardData, currency, institutionId, handleSyncStatus, isUpdating, producerUid }: ProducerDashboardContentProps) {

    const balance = dashboardData?.balance || 0;
    const toOperator = Math.abs(dashboardData?.operator_from_producer_transferable_balance || 0);
    const payable = dashboardData?.payable_balance || 0;

    const isToOperatorRisk = balance < toOperator;
    const isPayableRisk = balance < payable;

    return (

        <Box sx={{ transition: 'opacity 0.2s' }}>
            <Stack spacing={2} sx={{ mb: 3 }}>
                {dashboardData.balance && (isToOperatorRisk || isPayableRisk) && (
                    <Alert severity="warning" variant="filled">
                        <strong>Financial Risk:</strong> Wallet balance ({formatMoney(balance, currency)}) cannot cover pending
                        {isToOperatorRisk ? " Operator Transfers" : ""}
                        {isToOperatorRisk && isPayableRisk ? " &" : ""}
                        {isPayableRisk ? " Payable Invoices" : ""}.
                    </Alert>
                )}
                {dashboardData.institution_status && isStatusDesync(dashboardData.one_comm_status, dashboardData.institution_status) && (
                    <Alert severity="error" variant="filled" icon={<SyncProblemIcon fontSize="inherit" />}>
                        <strong>Desynchronization Detected:</strong> OneComm status is <strong>{dashboardData.one_comm_status}</strong> but Institution status is <strong>{dashboardData.institution_status}</strong>.
                    </Alert>
                )}
                {isApiError(dashboardData) && (
                    <ApiErrorAlert data={dashboardData} />
                )}
            </Stack>

            <ProducerBalances
                balance={dashboardData.balance}
                currency={currency}
                operator_from_producer_transferable_balance={dashboardData.operator_from_producer_transferable_balance}
                payable_balance={dashboardData.payable_balance}
                producer_from_escrow_transferable_balance={dashboardData.producer_from_escrow_transferable_balance}
            />

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} mb={4}>
                <AssociatedOperators associated_operators={dashboardData.associated_operators} />
                <Box sx={{ flex: 1.5 }}>
                    {dashboardData.documents &&
                        <KycDocuments documents={dashboardData.documents} />}
                </Box>
                <SyncStatus
                    handleSyncStatus={handleSyncStatus}
                    isUpdating={isUpdating}
                    one_comm_status={dashboardData.one_comm_status}
                    institution_status={dashboardData.institution_status}
                    institution={institutionId}
                />
            </Stack>

            <PayableInvoices
                invoices={dashboardData.payable_invoices || []}
                currency={currency}
            />

            {dashboardData.wallet_transactions &&
                <Transactions
                    walletTransactions={dashboardData.wallet_transactions || { transactions_in: [], transactions_out: [], transactions_p2_p: [] }}
                    currency={currency}
                    walletId={producerUid}
                />
            }

            <DashboardTransactions data={dashboardData.one_comm_pending_transactions} currency={currency} />

        </Box>
    );
}